import { CSSProperties, ReactNode } from 'react';
import { RoughNotation } from 'react-rough-notation';
import type {
  RoughNotationBracket,
  RoughNotationType,
} from './roughNotationTypes';

/**
 * Straight marks. rough-notation's own defaults (1.5, or 3 for `highlight`)
 * are far too wobbly over document text.
 */
export const DEFAULT_HIGHLIGHT_ROUGHNESS = 0;

export {
  ROUGH_NOTATION_BRACKETS,
  ROUGH_NOTATION_TYPES,
  type RoughNotationBracket,
  type RoughNotationType,
} from './roughNotationTypes';

/**
 * Configuration for RoughNotation appearance and animation.
 */
export type RoughNotationConfig = {
  /** Type of annotation to render */
  type: RoughNotationType;
  /** Color of the annotation */
  color?: string;
  /** Whether to animate the annotation */
  animate?: boolean;
  /** Delay before animation starts (ms) */
  animationDelay?: number;
  /** Duration of animation (ms) */
  animationDuration?: number;
  /** Width of the stroke */
  strokeWidth?: number;
  /** Padding around the content */
  padding?: number | [number, number] | [number, number, number, number];
  /** Bracket positions (for 'bracket' type) */
  brackets?: RoughNotationBracket[];
  /** Number of iterations for the annotation */
  iterations?: number;
  /** Whether to support multiline content */
  multiline?: boolean;
};

/**
 * Rectangle with position and dimensions.
 * Supports both LTWH format (left, top, width, height) and
 * Scaled format (x1, y1, x2, y2, width, height) from react-pdf-highlighter.
 */
export type HighlightRect = {
  width: number;
  height: number;
  /** Left position (LTWH format) */
  left?: number;
  /** Top position (LTWH format) */
  top?: number;
  /** X1 coordinate (Scaled format) */
  x1?: number;
  /** Y1 coordinate (Scaled format) */
  y1?: number;
  /** X2 coordinate (Scaled format) */
  x2?: number;
  /** Y2 coordinate (Scaled format) */
  y2?: number;
  /** Page number for multi-page documents */
  pageNumber?: number;
};

export type RoughNotationHighlightProps = {
  /** Array of rectangles to render annotations for */
  rects: HighlightRect[];
  /** RoughNotation configuration */
  config: RoughNotationConfig;
  /** Whether to show the annotation */
  show?: boolean;
  /** Roughness of the hand-drawn effect (0 = smooth, higher = rougher). Defaults to straight. */
  roughness?: number;
  /** Opacity of the container (useful for highlight type) */
  opacity?: number;
  /** Custom z-index for positioning */
  zIndex?: number;
  /** Render prop for custom child content inside RoughNotation */
  renderContent?: (rect: HighlightRect, index: number) => ReactNode;
  /** Render prop for additional elements per rect (e.g., badges, tooltips) */
  renderExtra?: (rect: HighlightRect, index: number) => ReactNode;
  /** Unique key prefix for list rendering */
  keyPrefix?: string;
  /** Custom class name for the container */
  className?: string;
  /** Custom styles for the container */
  style?: CSSProperties;
};

/**
 * Component for rendering RoughNotation annotations over PDF highlights.
 *
 * Handles the common pattern of iterating over rectangles and rendering
 * RoughNotation with absolute positioning. Supports customizable child
 * content and extra elements per rectangle.
 *
 * @param props - Component props
 * @returns Array of positioned RoughNotation elements
 *
 * @example
 * ```tsx
 * // Basic usage with default content
 * <RoughNotationHighlight
 *   rects={highlight.position.rects}
 *   config={{ type: 'highlight', color: '#ffeb3b' }}
 *   show={true}
 * />
 *
 * // With clickable content
 * <RoughNotationHighlight
 *   rects={rects}
 *   config={roughNotation}
 *   renderContent={(rect) => (
 *     <button
 *       onClick={handleClick}
 *       style={{ height: rect.height, width: rect.width }}
 *     />
 *   )}
 * />
 *
 * // With citation badge
 * <RoughNotationHighlight
 *   rects={rects}
 *   config={roughNotation}
 *   renderExtra={(rect, index) =>
 *     index === 0 && <CitationBadge index={1} position={{ left: rect.left - 20, top: rect.top - 5 }} />
 *   }
 * />
 * ```
 */
export const RoughNotationHighlight = ({
  rects,
  config,
  show = true,
  roughness = DEFAULT_HIGHLIGHT_ROUGHNESS,
  opacity,
  zIndex = 1000,
  renderContent,
  renderExtra,
  keyPrefix = 'rough-notation',
  className,
  style,
}: RoughNotationHighlightProps) => {
  return (
    <div className={className} style={style}>
      {rects.map((rect, index) => {
        // Support both LTWH format (left, top) and Scaled format (x1, y1)
        const left = rect.left ?? rect.x1 ?? 0;
        const top = rect.top ?? rect.y1 ?? 0;

        return (
          <div key={`${keyPrefix}-${index}-${JSON.stringify(config)}`}>
            {renderExtra?.(rect, index)}
            <div
              style={{
                position: 'absolute',
                zIndex,
                left,
                top,
                height: rect.height,
                width: rect.width,
                opacity,
              }}
            >
              <RoughNotation
                show={show}
                type={config.type}
                color={config.color}
                animate={config.animate}
                animationDelay={config.animationDelay}
                animationDuration={config.animationDuration}
                strokeWidth={config.strokeWidth}
                padding={config.padding}
                brackets={config.brackets}
                iterations={config.iterations}
                multiline={config.multiline}
                roughness={roughness}
              >
                {renderContent ? (
                  renderContent(rect, index)
                ) : (
                  <div style={{ height: rect.height, width: rect.width }} />
                )}
              </RoughNotation>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RoughNotationHighlight;
